import { Box, Typography } from '@mui/joy';
import { useTheme } from '@mui/joy/styles';
import { Plus } from '@phosphor-icons/react';
import { memo, MouseEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { Color, Level, Size, Variant } from 'theme/enums';

import { HoursInput } from './HoursInput';
import { ITimeLogCalendarDay } from './types';
import { useTimeLogIntervalContext } from './useTimeLogInterval';
import { getDayBackgroundColor, getDayColor, getHoverBackgroundColor } from './utils';

interface IProps {
  day: ITimeLogCalendarDay;
  totalDays: number;
  index: number;
}

export const Day = memo(({ day, totalDays, index }: IProps) => {
  const [editingDay, setEditingDay] = useState<string | null>(null);
  const [dayHours, setDayHours] = useState<number | null>(null);

  const theme = useTheme();
  const { t } = useTranslation();

  const { intervalStart, intervalEnd, selectedDates, onDayClick, onDayHover, previewDates } =
    useTimeLogIntervalContext();

  const isLastRow = index >= totalDays - 7;
  const isLastColumn = (index + 1) % 7 === 0;

  // Calculate interval status for this day
  const isInInterval = useMemo(
    () => selectedDates.some((date) => date.isSame(day.date, 'day')),
    [selectedDates, day.date]
  );
  const isIntervalStart = useMemo(
    () => intervalStart?.isSame(day.date, 'day') || false,
    [intervalStart, day.date]
  );
  const isIntervalEnd = useMemo(
    () => intervalEnd?.isSame(day.date, 'day') || false,
    [intervalEnd, day.date]
  );
  const isInPreview = useMemo(
    () => previewDates.some((date) => date.isSame(day.date, 'day')),
    [previewDates, day.date]
  );

  const handleDateCellClick = useCallback(
    (day: ITimeLogCalendarDay) => {
      if (!day.isCurrentMonth) {
        return;
      }

      // Handle interval selection
      onDayClick(day.date);
    },
    [onDayClick]
  );

  const onAddHours = useCallback((day: ITimeLogCalendarDay, event: MouseEvent) => {
    event.stopPropagation();
    setEditingDay(day.dateKey);
    setDayHours(null);
  }, []);

  const onEditHours = useCallback((day: ITimeLogCalendarDay, event: MouseEvent) => {
    event.stopPropagation();
    setEditingDay(day.dateKey);
    setDayHours(day.hours);
  }, []);

  const onCancelEditHours = useCallback(() => {
    setEditingDay(null);
    setDayHours(null);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (day.isCurrentMonth) {
      onDayHover(day.date);
    }
  }, [day.isCurrentMonth, day.date, onDayHover]);

  const handleMouseLeave = useCallback(() => {
    onDayHover(null);
  }, [onDayHover]);

  return (
    <Box
      onClick={() => handleDateCellClick(day)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        minHeight: 75,
        height: '100%',
        cursor: day.isCurrentMonth ? 'pointer' : 'default',
        position: 'relative',
        backgroundColor: getDayBackgroundColor(
          theme,
          isIntervalStart,
          isIntervalEnd,
          isInInterval,
          isInPreview
        ),
        borderRight: !isLastColumn ? '1px solid' : 'none',
        borderBottom: !isLastRow ? '1px solid' : 'none',
        borderColor: 'neutral.outlinedBorder',
        padding: '4px',
        transition: 'background-color 0.2s',
        boxShadow:
          isIntervalStart || isIntervalEnd
            ? (theme) => `inset 0 0 0 2px ${theme.palette.primary.solidBg}`
            : isInPreview
            ? (theme) => `inset 0 0 0 1px ${theme.palette.primary[300]}`
            : 'none',

        '&:hover': day.isCurrentMonth
          ? {
              backgroundColor: getHoverBackgroundColor(theme, isInInterval),
              boxShadow: `inset 0 0 0 1px ${theme.palette.primary.solidBg}`,

              '& .dayButtonContainer button': {
                opacity: 1,
              },
            }
          : {},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: day.isToday ? 'primary.500' : 'transparent',
        }}
      >
        <Typography
          level={Level.TITLE_SM}
          sx={{
            color: getDayColor(theme, day, isInInterval),
            fontWeight: day.isToday || isInInterval ? 600 : 500,
            lineHeight: 1,
          }}
        >
          {day.day}
        </Typography>
      </Box>

      <Box
        className="dayButtonContainer"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 'calc(100% - 2*6px)',
          height: '28px',
          margin: '0 6px',
        }}
      >
        {editingDay === day.dateKey ? (
          <HoursInput
            day={day}
            value={dayHours}
            setValue={setDayHours}
            onCancelEdit={onCancelEditHours}
          />
        ) : day.hours > 0 ? (
          <Typography
            onClick={(e) => onEditHours(day, e)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'primary.100',
              color: 'primary.600',
              fontSize: '14px',
              fontWeight: 600,
              borderRadius: '3px',
              cursor: 'pointer',
              width: '100%',
              minHeight: '28px',

              '&:hover': {
                backgroundColor: 'primary.200',
              },
            }}
          >
            {day.hours % 1 === 0 ? day.hours : day.hours.toFixed(2)} {t('labelHrs')}
          </Typography>
        ) : (
          day.isCurrentMonth && (
            <Button
              size={Size.SM}
              color={Color.PRIMARY}
              variant={Variant.SOFT}
              onClick={(e) => onAddHours(day, e)}
              icon={<Plus weight="bold" />}
              sx={{
                color: 'primary.500',
                borderRadius: '3px',
                width: '100%',
                minHeight: '28px',
                opacity: 0,
                transition: 'opacity 0.2s',
              }}
            />
          )
        )}
      </Box>
    </Box>
  );
});

Day.displayName = 'Day';
